import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import Fab from '@mui/material/Fab'
import Tooltip from '@mui/material/Tooltip'
import { rejectWithTimeLimit } from '@common/helpers/helpers'
import cn from 'classnames'
import { usePermissions } from '../../helpers/permissionContext'
import { useIsMobile, useIsTouch } from '../../hooks/useIsMobile'
import useIsOnline from '../../hooks/useIsOnline'
import Button from '../Common/Button'
import Icon from '../Common/Icon'
import IconButton from '../Common/IconButton'
import Text from '../Common/Text'
import TextField from '../Common/TextField'
import styles from './FeedbackWidget.module.scss'
import RegionScreenshotPreview from './components/RegionScreenshotPreview'
import RegionSelectOverlay from './components/RegionSelectOverlay'
import {
  closeFeedbackWidget,
  openFeedbackWidget,
  useIsFeedbackWidgetOpen,
} from './feedbackWidgetOpenStore'
import useArcFollowUps from './hooks/useArcFollowUps'
import useConsoleCapture from './hooks/useConsoleCapture'
import useFileAttachments from './hooks/useFileAttachments'
import useScreenRecording from './hooks/useScreenRecording'
import useScreenshotCapture, {
  canCaptureRegion,
  isPageTooLargeToAutoCapture,
} from './hooks/useScreenshotCapture'
import { PropsFromRedux } from './index'

type WidgetStep = 'form' | 'followups'

interface IFormData {
  title: string
  description: string
}

const INITIAL_FORM: IFormData = { title: '', description: '' }

// Annotates the failure rather than replacing it: an actionable server-side rejection has
// to survive a connection dropping afterwards.
const OFFLINE_ERROR = 'You appear to be offline — reconnect and submit again.'

// How long the form waits on a submit before handing control back. Nothing is cancelled;
// superagent's own deadline is five minutes, and a five-minute wait is the reported freeze.
const SUBMIT_TIMEOUT_MS = 45000

// Arc's follow-up questions are a nicety in front of the actual submit, so they get a much
// shorter leash than the submit itself: past this the feedback goes without them.
const FOLLOW_UPS_TIMEOUT_MS = 20000

const TIMEOUT_ERROR = 'Submitting is taking longer than expected. Please try again.'

const UNEXPECTED_ERROR = 'Something went wrong while submitting. Please try again.'

const newId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`

interface IOwnProps {
  /** MobileHeader is mounted: it supplies both the top chrome and the entry point. */
  hasMobileHeader: boolean
  /** MobileNav is mounted, so the bottom of the viewport is not ours. */
  hasMobileTabBar: boolean
}

export type IProps = PropsFromRedux & IOwnProps

const FeedbackWidget = ({
  isSubmitting,
  isSubmitted,
  submitError,
  createFeedback,
  generateFollowUps,
  failFeedbackSubmit,
  resetFeedbackSubmitted,
  hasMobileHeader,
  hasMobileTabBar,
}: IProps) => {
  const { isInternalUser } = usePermissions()
  const isMobile = useIsMobile()
  const isTouch = useIsTouch()
  const isOnline = useIsOnline()
  // Owned by the store: MobileHeader's profile menu opens it from outside.
  const isOpen = useIsFeedbackWidgetOpen()
  const [step, setStep] = useState<WidgetStep>('form')
  const [form, setForm] = useState<IFormData>(INITIAL_FORM)

  const consoleLogsRef = useConsoleCapture()
  const {
    isRecording,
    recordingBlob,
    formattedRecordingTime,
    startRecording,
    stopRecording,
    stopRecordingAndGetBlob,
    discardRecording,
    resetRecording,
  } = useScreenRecording()
  const {
    isSelectingRegion,
    isCapturingRegion,
    dragRect,
    regionScreenshot,
    hasRegionCaptureFailed,
    isGrabbingFrame,
    fullScreenshotRef,
    captureFullScreenshot,
    startSelectingRegion,
    clearRegionScreenshot,
    handleRegionMouseDown,
    handleRegionMouseMove,
    handleRegionMouseUp,
    resetScreenshots,
  } = useScreenshotCapture()
  const { files, fileInputRef, handleFileSelect, handleFilesChange, handleRemoveFile, resetFiles } =
    useFileAttachments()
  const {
    followUpQuestions,
    followUpAnswers,
    isGeneratingFollowUps,
    setIsGeneratingFollowUps,
    loadingMessageIndex,
    loadingMessage,
    transcript,
    startFollowUpQuestions,
    handleAnswerChange,
    resetFollowUps,
  } = useArcFollowUps()

  // Guard each entry point into the submit flow against a second click landing before the
  // first has re-rendered the button as disabled.
  const isContinueInFlightRef = useRef(false)
  const isSubmitInFlightRef = useRef(false)
  // One key per report, reused by every retry of it — the server dedupes on it, which is what
  // stops a retry of an abandoned (uncancellable) submit filing the report twice.
  const submissionKeyRef = useRef(newId())

  // The store outlives this component; unmounting while open would leave every MUI
  // modal's focus trap off.
  useEffect(() => () => closeFeedbackWidget(), [])

  const resetState = useCallback(() => {
    setStep('form')
    setForm(INITIAL_FORM)
    resetScreenshots()
    resetFiles()
    resetRecording()
    resetFollowUps()
    submissionKeyRef.current = newId()
    // Both guards belonged to the report that just ended; leaving them set makes the reopened
    // form's Submit a no-op until the abandoned attempt's deadline expires.
    isContinueInFlightRef.current = false
    isSubmitInFlightRef.current = false
  }, [resetScreenshots, resetFiles, resetRecording, resetFollowUps])

  const handleClose = useCallback(() => {
    closeFeedbackWidget()
    resetState()
    resetFeedbackSubmitted()
  }, [resetState, resetFeedbackSubmitted])

  const handleFieldChange = useCallback((field: keyof IFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }, [])

  const isFormValid = useMemo(() => form.title.trim().length > 0, [form.title])
  const isAutoCaptureSkipped = useMemo(() => isOpen && isPageTooLargeToAutoCapture(), [isOpen])
  const isRegionCaptureBlocked = useMemo(() => isOpen && !canCaptureRegion(), [isOpen])
  const recordTooltipTitle = recordingBlob ? 'Screen recording attached' : 'Record screen'
  const screenshotTooltipTitle = isRegionCaptureBlocked
    ? 'This page is too large to capture in this browser'
    : 'Select an area to capture'

  const renderError = () =>
    submitError && (
      <div className={styles.errorMessage}>
        {submitError}
        {!isOnline && <span className={styles.errorHint}>{OFFLINE_ERROR}</span>}
      </div>
    )

  // Takes the key rather than reading the ref: flushing a recording awaits, and a close during
  // that await would mint a new key — filing this report's content under the next report's key.
  const buildSubmission = useCallback(
    async (submissionKey: string) => {
      // A recording may still be running when the user hits Submit — stop it and
      // attach the result behind the scenes instead of silently dropping it.
      const recording = isRecording ? await stopRecordingAndGetBlob() : recordingBlob

      const formData = new FormData()
      formData.append('title', form.title)
      formData.append('description', form.description)
      formData.append('pageUrl', window.location.href)
      formData.append('submissionKey', submissionKey)

      // Both are sized to fit MAX_SCREENSHOT_SIZE before they are encoded; see useScreenshotCapture.
      if (fullScreenshotRef.current) {
        formData.append('screenshotData', fullScreenshotRef.current)
      }
      if (regionScreenshot) {
        formData.append('regionScreenshotData', regionScreenshot)
      }
      if (consoleLogsRef.current.length > 0) {
        formData.append('consoleLogs', JSON.stringify(consoleLogsRef.current))
      }
      if (transcript.length > 0) {
        formData.append('arcTranscript', JSON.stringify(transcript))
      }

      files.forEach((file) => formData.append('files', file))
      if (recording) {
        formData.append('files', recording, `recording-${Date.now()}.webm`)
      }

      return formData
    },
    [
      form,
      regionScreenshot,
      fullScreenshotRef,
      consoleLogsRef,
      transcript,
      files,
      isRecording,
      recordingBlob,
      stopRecordingAndGetBlob,
    ],
  )

  /**
   * Submits, and always comes back — with a success, an error, or a timeout. The loading
   * overlay is driven by the redux request still being in flight, so every exit has to
   * resolve or explicitly fail it. The ref is the double-submit guard: `isSubmitting` only
   * arrives via a re-render, which is exactly what a busy main thread delays.
   */
  const submitFeedback = useCallback(
    async (reportKey: string) => {
      if (isSubmitInFlightRef.current) return
      isSubmitInFlightRef.current = true
      // Identifies this attempt to the reducer, which retires it on the way out.
      const attemptId = newId()
      // `resetState` mints a new key, so a key that has moved since `reportKey` means the panel was
      // closed — or reopened on a fresh report — and nothing here belongs to this attempt any more.
      const isAbandoned = () => submissionKeyRef.current !== reportKey
      try {
        // One deadline over assembly *and* the request: flushing a screen recording waits on a
        // MediaRecorder event that may never arrive, which strands the form just as surely.
        await rejectWithTimeLimit(
          SUBMIT_TIMEOUT_MS,
          buildSubmission(reportKey).then((formData) => createFeedback(formData, attemptId)),
        )
      } catch (err) {
        // The redux request is either absent or still pending, so nothing else clears the form —
        // but only while the report is still on screen. Closing it already cleared both.
        if (!isAbandoned()) {
          failFeedbackSubmit(err === 'ERR_TIMEOUT' ? TIMEOUT_ERROR : UNEXPECTED_ERROR, attemptId)
        }
      } finally {
        if (!isAbandoned()) {
          isSubmitInFlightRef.current = false
        }
      }
    },
    [buildSubmission, createFeedback, failFeedbackSubmit],
  )

  const handleContinue = useCallback(async () => {
    if (!isFormValid || isContinueInFlightRef.current) return
    isContinueInFlightRef.current = true
    // Closing mid-flow mints a new key: from then on this invocation must neither submit nor
    // touch state that now belongs to the reopened report.
    const reportKey = submissionKeyRef.current
    const isAbandoned = () => submissionKeyRef.current !== reportKey

    setIsGeneratingFollowUps(true)
    try {
      // Captured here rather than when the panel opens: rendering a dense page is heavy enough
      // to take the tab down, so it only happens once the user has asked to send something.
      await captureFullScreenshot()
      if (isAbandoned()) return

      // Arc follow-up questions are an internal-only step — clients and
      // prospects submit directly.
      if (!isInternalUser) {
        await submitFeedback(reportKey)
        return
      }

      try {
        // Bounded: an unanswered model call would otherwise hold the "Arc is reviewing"
        // overlay for five minutes. The timeout rejects into the give-up-and-send path below.
        const res = (await rejectWithTimeLimit(
          FOLLOW_UPS_TIMEOUT_MS,
          generateFollowUps({
            title: form.title,
            description: form.description,
            pageUrl: window.location.href,
            // Prefer the highlighted region (most focused) and fall back to the
            // full-page capture so Arc can ground its questions in what the user saw.
            screenshotData: regionScreenshot || fullScreenshotRef.current || undefined,
          }),
        )) as { data?: { questions?: string[] } }
        if (isAbandoned()) return
        const questions = res?.data?.questions ?? []
        if (questions.length > 0) {
          startFollowUpQuestions(questions)
          setStep('followups')
          return
        }
      } catch {
        // best-effort — fall through to submit
      }
      if (isAbandoned()) return
      // Awaiting keeps the loading overlay up across the hand-off to `isSubmitting`, so the
      // form never flashes back in between.
      await submitFeedback(reportKey)
    } finally {
      if (!isAbandoned()) {
        setIsGeneratingFollowUps(false)
        isContinueInFlightRef.current = false
      }
    }
  }, [
    isFormValid,
    setIsGeneratingFollowUps,
    isInternalUser,
    captureFullScreenshot,
    generateFollowUps,
    form.title,
    form.description,
    regionScreenshot,
    fullScreenshotRef,
    startFollowUpQuestions,
    submitFeedback,
  ])

  // ---------- Render ----------
  const aboveTabBar = cn({ [styles.aboveTabBar]: hasMobileTabBar })
  const modalClassName = cn(styles.modal, {
    [styles.modalUnderHeader]: hasMobileHeader,
    [styles.modalAboveTabBar]: hasMobileTabBar,
  })

  // Internal users get the floating pill (bottom-right is Atlas chat for them);
  // clients/prospects get a bottom-left tab matching the MessageRmBox tab. Mobile defers
  // to the profile menu — except where MobileHeader is absent and there is no other way in.
  const renderLauncher = (mode: 'open' | 'close') => {
    if (isMobile && hasMobileHeader) {
      return null
    }
    if (!isInternalUser) {
      return (
        <button
          type="button"
          className={cn(styles.launcherTab, aboveTabBar)}
          data-feedback-widget
          onClick={mode === 'open' ? openFeedbackWidget : handleClose}
        >
          {mode === 'open' ? (
            'Feedback'
          ) : (
            <Icon name="x" size="sm" aria-hidden={false} aria-label="Close feedback" />
          )}
        </button>
      )
    }
    return (
      <div className={cn(styles.launcher, aboveTabBar)} data-feedback-widget>
        {mode === 'open' ? (
          <Fab variant="extended" onClick={openFeedbackWidget} className={styles.feedbackButton}>
            Feedback
          </Fab>
        ) : (
          <Fab size="medium" onClick={handleClose} className={styles.closeButton}>
            <Icon name="x" size="sm" aria-hidden={false} aria-label="Close feedback" />
          </Fab>
        )}
      </div>
    )
  }

  if (isOpen && isSubmitted) {
    return (
      <>
        <div className={modalClassName} data-feedback-widget>
          <div className={styles.header}>
            <Text variant="title">Feedback</Text>
            <IconButton title="Close feedback form" size="small" onClick={handleClose}>
              <Icon name="x" size="sm" />
            </IconButton>
          </div>
          <div className={styles.successView}>
            <div className={styles.successIcon}>&#10003;</div>
            <Text variant="title" casing="sentence">
              Thank you!
            </Text>
            <Text variant="bodySm">Your feedback has been submitted successfully.</Text>
            <Button size="small" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
        {renderLauncher('close')}
      </>
    )
  }

  if (!isOpen) {
    return renderLauncher('open')
  }

  // A frame of the tab is being taken from its real pixels, so nothing of the widget may show.
  if (isGrabbingFrame) {
    return null
  }

  // While selecting a screenshot region, show only the selection overlay — the
  // panel/launcher would block the area being captured. (The DOM screenshot already
  // filters the widget out via `data-feedback-widget`; this is about the user having
  // an unobstructed view to drag over.)
  if (isSelectingRegion) {
    return (
      <RegionSelectOverlay
        dragRect={dragRect}
        onMouseDown={handleRegionMouseDown}
        onMouseMove={handleRegionMouseMove}
        onMouseUp={() => void handleRegionMouseUp()}
      />
    )
  }

  // While recording the screen, collapse to a compact pill so the full panel never
  // appears in the recording (getDisplayMedia captures real screen pixels, unlike the
  // DOM screenshot). The user stops here, via the browser's native "Stop sharing" bar,
  // or at the 120s cap — the full panel returns once recording ends.
  if (isRecording) {
    return (
      <div className={cn(styles.recordingBar, aboveTabBar)} data-feedback-widget>
        <span className={styles.recordingDot} />
        <span className={styles.recordingTimer}>{formattedRecordingTime}</span>
        <Button size="small" onClick={stopRecording}>
          Stop recording
        </Button>
      </div>
    )
  }

  // Keep the loading overlay up across both the Arc review and the submit so the
  // form never flashes back in between (fixes the brief "form → thank you" blip).
  const showLoadingOverlay = isGeneratingFollowUps || isSubmitting
  // Which half of that overlay we are in — clients and prospects skip the Arc review.
  const isSaving = isSubmitting || !isInternalUser

  return (
    <>
      <div className={modalClassName} data-feedback-widget>
        <div className={styles.header}>
          <Text variant="title">
            {showLoadingOverlay
              ? isSaving
                ? 'Submitting'
                : 'Reviewing your feedback'
              : step === 'followups'
                ? 'A few quick questions'
                : 'Send feedback'}
          </Text>
          <IconButton title="Close feedback form" size="small" onClick={handleClose}>
            <Icon name="x" size="sm" />
          </IconButton>
        </div>

        {showLoadingOverlay ? (
          <div className={styles.arcLoading}>
            <div className={styles.arcLoadingOrb}>
              <Icon name="message-text" size="lg" />
            </div>
            <Text variant="bodyLg" tone="primary" className={styles.arcLoadingTitle}>
              {isSaving ? 'Saving your feedback' : 'Arc is reviewing your feedback'}
            </Text>
            <Text
              variant="bodySm"
              tone="tertiary"
              key={loadingMessageIndex}
              className={styles.arcLoadingMessage}
            >
              {isSaving ? 'Almost done…' : loadingMessage}
            </Text>
          </div>
        ) : step === 'form' ? (
          <>
            <div className={styles.body}>
              <div className={styles.captureTools}>
                {/* Pointer-only: the region grab is a mouse drag, and Record goes through
                    getDisplayMedia, which no touch browser implements. */}
                {!isTouch && (
                  <>
                    <Tooltip title={screenshotTooltipTitle}>
                      {/* A disabled button fires no pointer events, so the span carries the hover
                          and the button keeps the accessible name. */}
                      <span>
                        <button
                          type="button"
                          className={cn(styles.captureTool, {
                            [styles.active]: !!regionScreenshot,
                          })}
                          onClick={() => void startSelectingRegion()}
                          disabled={isRegionCaptureBlocked}
                          aria-label={screenshotTooltipTitle}
                        >
                          <Icon name="scan" size="sm" />
                          <Text variant="button">Screenshot</Text>
                        </button>
                      </span>
                    </Tooltip>

                    <Tooltip title={recordTooltipTitle}>
                      <span>
                        <button
                          type="button"
                          className={cn(styles.captureTool, { [styles.active]: !!recordingBlob })}
                          onClick={startRecording}
                          disabled={!!recordingBlob}
                          aria-label={recordTooltipTitle}
                        >
                          <Icon name="video" size="sm" />
                          <Text variant="button">Record</Text>
                        </button>
                      </span>
                    </Tooltip>
                  </>
                )}

                <Tooltip title="Attach files">
                  <button type="button" className={styles.captureTool} onClick={handleFileSelect}>
                    <Icon name="attach" size="sm" />
                    {/* Touch never shows the tooltip, so there the label is the only name. */}
                    {isTouch && 'Attach'}
                  </button>
                </Tooltip>
              </div>

              <Text variant="caption" tone="tertiary" component="p" className={styles.captureHint}>
                {isRegionCaptureBlocked
                  ? 'This page is too large to capture. Attach a screenshot file instead.'
                  : isAutoCaptureSkipped
                    ? 'This page is too large to capture automatically. Attach a screenshot or file instead.'
                    : 'Your current screen is attached automatically when you submit.'}
              </Text>

              <RegionScreenshotPreview
                isCapturing={isCapturingRegion}
                hasFailed={hasRegionCaptureFailed}
                screenshot={regionScreenshot}
                failedMessage="Couldn't capture this screen. Try again, or attach a file."
                onClear={clearRegionScreenshot}
                className={styles.screenshotPreview}
              />

              <div className={styles.formGroup}>
                <Text variant="fieldLabel" component="label" htmlFor="feedback-title">
                  Title <span className={styles.required}>*</span>
                </Text>
                <TextField
                  id="feedback-title"
                  name="title"
                  useFinalForm={false}
                  size="small"
                  fullWidth
                  placeholder="Brief summary of the issue or idea"
                  required
                  value={form.title}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleFieldChange('title', e.target.value)
                  }
                />
              </div>

              <div className={styles.formGroup}>
                <Text variant="fieldLabel" component="label" htmlFor="feedback-description">
                  Description
                </Text>
                <TextField
                  id="feedback-description"
                  name="description"
                  useFinalForm={false}
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="What happened? What did you expect?"
                  value={form.description}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleFieldChange('description', e.target.value)
                  }
                />
              </div>

              {files.length > 0 && (
                <div className={styles.fileList}>
                  {files.map((file, i) => (
                    <div key={`${file.name}-${i}`} className={styles.fileChip}>
                      {file.name}
                      <IconButton
                        title={`Remove ${file.name}`}
                        box="xs"
                        onClick={() => handleRemoveFile(i)}
                      >
                        <Icon name="x" size="xs" />
                      </IconButton>
                    </div>
                  ))}
                </div>
              )}

              {recordingBlob && (
                <div className={styles.fileList}>
                  <div className={styles.fileChip}>
                    <Text variant="caption" component="span">
                      Screen recording ({formattedRecordingTime})
                    </Text>
                    <IconButton title="Discard recording" box="xs" onClick={discardRecording}>
                      <Icon name="x" size="xs" />
                    </IconButton>
                  </div>
                </div>
              )}

              {renderError()}

              <input
                ref={fileInputRef}
                type="file"
                multiple
                className={styles.fileInput}
                onChange={handleFilesChange}
              />
            </div>

            <div className={styles.footer}>
              <Button secondary size="small" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                size="small"
                onClick={() => void handleContinue()}
                // A region still capturing lands after the submission is built, and would be dropped.
                disabled={!isFormValid || isGeneratingFollowUps || isCapturingRegion}
                isLoading={isGeneratingFollowUps}
              >
                Submit
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.body}>
              <Text variant="bodySm" tone="primary" className={styles.followUpIntro}>
                Arc has a couple of follow-up questions to help us act on your feedback. Answer what
                you can, or skip.
              </Text>
              {followUpQuestions.map((question, i) => (
                <div key={i} className={styles.formGroup}>
                  <Text
                    variant="fieldLabel"
                    component="label"
                    casing="none"
                    htmlFor={`feedback-followup-${i}`}
                  >
                    {question}
                  </Text>
                  <TextField
                    id={`feedback-followup-${i}`}
                    name={`followup-${i}`}
                    useFinalForm={false}
                    size="small"
                    fullWidth
                    multiline
                    placeholder="Your answer"
                    value={followUpAnswers[i] || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleAnswerChange(i, e.target.value)
                    }
                  />
                </div>
              ))}

              {renderError()}
            </div>

            <div className={styles.footer}>
              <Button
                secondary
                size="small"
                onClick={() => void submitFeedback(submissionKeyRef.current)}
                disabled={isSubmitting}
              >
                Skip
              </Button>
              <Button
                size="small"
                onClick={() => void submitFeedback(submissionKeyRef.current)}
                disabled={isSubmitting}
                isLoading={isSubmitting}
              >
                Submit
              </Button>
            </div>
          </>
        )}
      </div>

      {renderLauncher('close')}
    </>
  )
}

export default FeedbackWidget
